/**
 * CategoryCards — "Browse by topic": the help categories that have a current article (BR-14), each with
 * its article count. The topic name and count open the topic's full article list (US-6); the top article
 * stays a secondary link. A card grid on the help center, a compact row list in the panel.
 */

import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Skeleton } from '../ui/skeleton';
import { helpArticlePath, helpTopicPath, useHelpCategories } from '../../lib/api.help';
import { HelpEyebrow, HelpLoadError, RowsSkeleton, TopicRow } from './ArticleList';

interface CategoryCardsProps {
  variant: 'grid' | 'list';
  /** In the panel a topic opens in place; without it the links go to /help/topics/<category>. */
  onSelectTopic?: (category: string, label: string) => void;
}

function articleCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'article' : 'articles'}`;
}

export function CategoryCards({ variant, onSelectTopic }: CategoryCardsProps) {
  const { data, isLoading, isError, refetch } = useHelpCategories(true);

  if (isLoading) {
    return variant === 'grid' ? (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="help-categories-loading" aria-busy="true">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-24 rounded-xl" />
        ))}
      </div>
    ) : (
      <RowsSkeleton data-testid="help-categories-loading" />
    );
  }
  if (isError) {
    return (
      <HelpLoadError
        message="We couldn't load the help topics."
        onRetry={() => void refetch()}
        data-testid="help-categories-error"
      />
    );
  }
  if (!data || data.length === 0) return null;

  if (variant === 'list') {
    return (
      <section aria-labelledby="help-browse-by-topic" className="flex flex-col" data-testid="help-categories">
        <HelpEyebrow id="help-browse-by-topic">Browse by topic</HelpEyebrow>
        {data.map((category) => (
          <TopicRow
            key={category.category}
            category={category.category}
            title={category.label}
            excerpt={`${articleCountLabel(category.articleCount)} · ${category.topArticle.title}`}
            onOpen={onSelectTopic && (() => onSelectTopic(category.category, category.label))}
            data-testid={`help-category-${category.category}`}
          />
        ))}
      </section>
    );
  }

  return (
    <section aria-labelledby="help-browse-by-topic" className="flex flex-col gap-3" data-testid="help-categories">
      <h2 id="help-browse-by-topic" className="text-lg font-semibold text-foreground">
        Browse by topic
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((category) => (
          <div
            key={category.category}
            data-testid={`help-category-${category.category}`}
            className="flex flex-col rounded-xl border border-border bg-card"
          >
            <Link
              to={helpTopicPath(category.category)}
              data-testid={`help-category-${category.category}-open`}
              className="flex min-h-[56px] items-center gap-2 rounded-t-xl p-[var(--card-padding)] pb-2 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-semibold text-foreground">{category.label}</span>
                <span className="text-xs text-muted-foreground">{articleCountLabel(category.articleCount)}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </Link>
            <Link
              to={helpArticlePath(category.topArticle.id)}
              data-testid={`help-category-${category.category}-top-article`}
              aria-label={`Start with ${category.topArticle.title}`}
              className="mx-[var(--card-padding)] mb-1 flex min-h-[44px] items-center text-[13px] font-medium text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {category.topArticle.title}
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}
